<template>
  <div>
    <div class="header">
      <div class="container-fluid">

        <b-row class="align-items-center mt-3 mb-2">
          <b-col>
            <span class="text-muted" v-if="usuariosFiltrados.length !== usuarios.length">
              {{ usuariosFiltrados.length }} / {{ usuarios.length }} {{ $t('usuarios') }}
            </span>
            <span class="text-muted" v-else>{{ usuarios.length }} {{ $t('usuarios') }}</span>
          </b-col>
          <b-col class="text-right">
            <!-- La ruta usuario-crear solo admite admin y operaciones: sin este
                 filtro, un perfil de solo lectura acaba redirigido a /error. -->
            <router-link v-if="puedeEscribir" :to="{ name: 'usuario-crear' }">
              <b-button variant="primary" size="sm">
                <i class="ni ni-fat-add mr-1"></i> {{ $t('crear_usuario') }}
              </b-button>
            </router-link>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">
          {{ error }}
        </b-alert>

        <div v-if="cargando" class="text-center text-muted mt-5">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="usuarios.length" class="card shadow" style="margin-bottom: 60px;">
          <div class="tabla-scroll-fixed" ref="tablaUsuarios">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th class="th-ordenable" @click="ordenarPor('id')">
                    <input class="th-search-input" v-model="filterCol.id" @click.stop placeholder="Filtrar…" />
                    <span class="th-label">ID <i :class="iconoOrden('id')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('nombre')">
                    <input class="th-search-input" v-model="filterCol.nombre" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('nombre') }} <i :class="iconoOrden('nombre')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('apellidoP')">
                    <input class="th-search-input" v-model="filterCol.apellido" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('apellido') }} <i :class="iconoOrden('apellidoP')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('rol')">
                    <input class="th-search-input" v-model="filterCol.rol" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('rol') }} <i :class="iconoOrden('rol')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:180px;" @click="ordenarPor('username')">
                    <input class="th-search-input" v-model="filterCol.username" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('username') }} <i :class="iconoOrden('username')"></i></span>
                  </th>
                  <th class="th-ordenable" style="width:120px;" @click="ordenarPor('activo')">
                    <select class="th-search-input" v-model="filterCol.estado" @click.stop>
                      <option value="activos">{{ $t('activos') }}</option>
                      <option value="inactivos">{{ $t('inactivos') }}</option>
                      <option value="todos">{{ $t('todos') }}</option>
                    </select>
                    <span class="th-label">{{ $t('estado') }} <i :class="iconoOrden('activo')"></i></span>
                  </th>
                  <th style="width:160px;"></th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarPor('empresa')">
                    <input class="th-search-input" v-model="filterCol.empresa" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('empresa') }} <i :class="iconoOrden('empresa')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in usuariosPaginados" :key="u.id">
                  <td>{{ u.id }}</td>
                  <td>{{ u.nombre }}</td>
                  <td>{{ u.apellidoP }}</td>
                  <td>{{ u.rol }}</td>
                  <td>{{ u.username }}</td>
                  <td class="text-center">
                    <b-badge :variant="u.activo ? 'success' : 'secondary'">
                      {{ u.activo ? $t('activo') : $t('inactivo') }}
                    </b-badge>
                  </td>
                  <td class="text-center">
                    <router-link :to="{ name: 'usuario-detalle', params: { id: u.id } }">
                      <b-button size="sm" variant="outline-primary" class="mr-1" :title="$t('editar')">
                        <i class="fas fa-pencil-alt"></i>
                      </b-button>
                    </router-link>
                    <b-button v-if="puedeEscribir && u.activo" size="sm" variant="outline-danger"
                      @click="eliminarUsuario(u.id)" :title="$t('desactivar')">
                      <i class="fas fa-trash"></i>
                    </b-button>
                    <b-button v-if="puedeEscribir && !u.activo" size="sm" variant="outline-success"
                      @click="reactivarUsuario(u)" :title="$t('reactivar')">
                      <i class="fas fa-undo"></i>
                    </b-button>
                  </td>
                  <td>{{ formatEmpresa(u.empresa) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="pagination-floating-bar" v-if="usuarios.length">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('items_por_pagina') || 'Items / pág.' }}</label>
            <select v-model.number="pageSize" @change="currentPage = 1" class="form-control form-control-sm" style="width:70px;">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="999999">Todas</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') || 'Anterior' }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') || 'Página' }} {{ currentPage }} {{ $t('de') || 'de' }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ usuariosFiltrados.length }} {{ $t('resultados') || 'resultados' }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
              {{ $t('siguiente') || 'Siguiente' }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:6px;">
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('left')" title="Scroll izquierda"><i class="fas fa-chevron-left"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('right')" title="Scroll derecha"><i class="fas fa-chevron-right"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('top')" title="Ir al inicio"><i class="fas fa-chevron-up"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('bottom')" title="Ir al final"><i class="fas fa-chevron-down"></i></button>
          </div>
        </div>

        <div v-else-if="!cargando" class="text-center text-muted mt-5">
          <i class="fas fa-users fa-3x mb-3"></i>
          <p>{{ $t('sin_usuarios') || 'No hay usuarios' }}</p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import userService from '@/api/userService';
import Swal from 'sweetalert2';

export default {
  name: 'UsuarioView',
  data() {
    return {
      usuarios: [],
      cargando: false,
      error: '',
      ordenColumna: null,
      ordenDireccion: 'asc',
      filterCol: {
        id: '',
        nombre: '',
        apellido: '',
        rol: '',
        username: '',
        empresa: '',
        // El listado trae activos e inactivos; por defecto se muestran solo los activos.
        estado: 'activos',
      },
      currentPage: 1,
      pageSize: 50,
    };
  },

  watch: {
    filterCol: { deep: true, handler() { this.currentPage = 1; } },
  },

  mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this._scrollHandler = this._onWindowScroll.bind(this);
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    this.cargarUsuarios();
  },

  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
    window.removeEventListener('scroll', this._scrollHandler);
  },

  computed: {
    usuariosFiltrados() {
      const fc = this.filterCol;
      let lista = this.usuarios;
      if (fc.id)       lista = lista.filter(u => String(u.id || '').toLowerCase().includes(fc.id.toLowerCase()));
      if (fc.nombre)   lista = lista.filter(u => (u.nombre || '').toLowerCase().includes(fc.nombre.toLowerCase()));
      if (fc.apellido) lista = lista.filter(u => (u.apellidoP || '').toLowerCase().includes(fc.apellido.toLowerCase()));
      if (fc.rol)      lista = lista.filter(u => (u.rol || '').toLowerCase().includes(fc.rol.toLowerCase()));
      if (fc.username) lista = lista.filter(u => (u.username || '').toLowerCase().includes(fc.username.toLowerCase()));
      if (fc.empresa)  lista = lista.filter(u => (u.empresa || '').toLowerCase().includes(fc.empresa.toLowerCase()));
      if (fc.estado === 'activos')   lista = lista.filter(u => u.activo);
      if (fc.estado === 'inactivos') lista = lista.filter(u => !u.activo);
      return lista;
    },
    usuariosOrdenados() {
      if (!this.ordenColumna) return this.usuariosFiltrados;
      const sorted = [...this.usuariosFiltrados];
      sorted.sort((a, b) => {
        let vA = a[this.ordenColumna];
        let vB = b[this.ordenColumna];
        if (vA == null) vA = '';
        if (vB == null) vB = '';
        if (typeof vA === 'number' && typeof vB === 'number') return this.ordenDireccion === 'asc' ? vA - vB : vB - vA;
        vA = String(vA).toLowerCase();
        vB = String(vB).toLowerCase();
        if (vA < vB) return this.ordenDireccion === 'asc' ? -1 : 1;
        if (vA > vB) return this.ordenDireccion === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    totalPages() {
      if (this.pageSize >= 999999) return 1;
      return Math.max(1, Math.ceil(this.usuariosOrdenados.length / this.pageSize));
    },
    usuariosPaginados() {
      if (this.pageSize >= 999999) return this.usuariosOrdenados;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.usuariosOrdenados.slice(start, start + this.pageSize);
    },
  },

  methods: {
    formatEmpresa(empresa) {
      if (!empresa) return '-';
      return empresa.replace(/<br\s*\/?>/gi, ' | ');
    },

    async cargarUsuarios() {
      this.cargando = true;
      this.error = '';
      try {
        this.usuarios = await userService.getAll();
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || this.$t('error');
      } finally {
        this.cargando = false;
      }
    },

    eliminarUsuario(id) {
      if (!this.puedeEscribir) return;
      Swal.fire({
        title: this.$t('esta_seguro'),
        text: this.$t('eliminar_usuario_texto'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('eliminar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await userService.deleteBy(id);
            this.usuarios = await userService.getAll();
            Swal.fire({ text: this.$t('eliminado_correctamente'), icon: 'success', timer: 2000, showConfirmButton: false });
          } catch {
            this.error = this.$t('error_desconocido');
          }
        }
      });
    },

    reactivarUsuario(u) {
      if (!this.puedeEscribir) return;
      Swal.fire({
        title: this.$t('esta_seguro'),
        text: this.$t('reactivar_usuario_texto'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('reactivar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#2dce89',
        cancelButtonColor: '#6c757d',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await userService.cambiarEstado(u.id, true);
            this.usuarios = await userService.getAll();
            Swal.fire({ text: this.$t('reactivado_correctamente'), icon: 'success', timer: 2000, showConfirmButton: false });
          } catch (e) {
            this.error = (e && e.response && e.response.data && e.response.data.message) || this.$t('error_desconocido');
          }
        }
      });
    },

    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
      this.currentPage = 1;
    },

    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    scrollTabla(dir) {
      const el = this.$refs.tablaUsuarios;
      const px = 300;
      if (dir === 'left')   el && el.scrollBy({ left: -px, behavior: 'smooth' });
      if (dir === 'right')  el && el.scrollBy({ left:  px, behavior: 'smooth' });
      if (dir === 'top')    window.scrollTo({ top: 0, behavior: 'smooth' });
      if (dir === 'bottom') window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },

    _onWindowScroll() {
      const cont = this.$refs.tablaUsuarios;
      if (!cont) return;
      const thead = cont.querySelector('thead.thead-sticky');
      if (!thead) return;
      const top = cont.getBoundingClientRect().top;
      thead.style.transform = top < 0 ? `translateY(${-top}px)` : '';
    },
  },
};
</script>

<style scoped>
.divisor {
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  padding: 15px;
  background: #6172e9;
  border-radius: 10px;
}

.card {
  border-radius: 10px;
  overflow: hidden;
}

.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

.btn-scroll-nav {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  background: #f8f9fa;
  color: #495057;
  border-radius: 6px;
}
.btn-scroll-nav:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
